import { LICENSE_EXPORT_LOG_BULK_TEMPLATE } from '../../bulk-action-templates';
import type {
	ListBuilderColumn,
	ListBuilderConfiguration,
	ListBuilderList,
} from '../../types';

/**
 * Compliance > Sent to ERP (PD-17390) — every license push Workd's ERP
 * integration made (Blue Link, NetSuite, DOit): what was sent, what the ERP
 * held before where it says so, the ERP's answer, and the acknowledgement a
 * compliance user records against a change or a failed push.
 *
 * Rows come from `GET /api/license-export-log` (crm-api
 * LicenseExportLogService): one `license_export_log` row each, paged, sorted
 * and filtered on the server. Every display value is worked out by the
 * endpoint (labels, tones, the status, whether a number or date changed),
 * because config columns render field values verbatim.
 *
 * Two tabs of one list: Unacknowledged (the default; locks the status filter
 * to rows waiting for someone) and All. The "Was → Now" reading follows the
 * Recently Fixed list: what was sent, over what the ERP held before.
 */

export const LICENSE_EXPORT_LOG_LIST_KEY = 'license_export_log';

/** Where the list lives. The menu and the dashboard link here. */
export const LICENSE_EXPORT_LOG_PATH = '/compliance/erp-activity';

/**
 * The tab ids. Part of the URL contract — a bookmarked or shared link carries
 * one — so a change breaks saved links; the config test pins them.
 */
export const LICENSE_EXPORT_LOG_LIST_IDS = {
	unacknowledged: 'license-export-log-unacknowledged',
	all: 'license-export-log-all',
} as const;

const OBJ = 'license_export_log';
const plain = { size: 'sm' as const, colorRule: { type: 'default' as const } };
const muted = {
	size: 'xs' as const,
	colorRule: { type: 'theme' as const, themeColor: 'textSecondary' as const },
};

type FieldConfig = ListBuilderColumn['fields'][number];

const lineBreak = (id: string): FieldConfig => ({
	id,
	dataConfig: { type: 'custom_text', customText: '' },
	displayStyle: 'line_break',
});

const field = (
	id: string,
	fieldName: string,
	style: FieldConfig['styleConfig'],
	displayStyle: FieldConfig['displayStyle'] = 'text',
): FieldConfig => ({
	id,
	dataConfig: { type: 'field', field: { fieldName, objectTable: OBJ } },
	styleConfig: style,
	displayStyle,
});

/** A field shown only when the row has a value for it. */
const optionalField = (
	id: string,
	fieldName: string,
	style: FieldConfig['styleConfig'],
	displayStyle: FieldConfig['displayStyle'] = 'text',
): FieldConfig => ({
	...field(id, fieldName, style, displayStyle),
	conditionalVisibility: { field: fieldName, condition: 'is_not_null' },
});

/** Literal words shown only when a row flag is true. */
const labelWhen = (
	id: string,
	text: string,
	flag: string,
	style: FieldConfig['styleConfig'] = muted,
): FieldConfig => ({
	id,
	dataConfig: { type: 'custom_text', customText: text },
	styleConfig: style,
	displayStyle: 'text',
	conditionalVisibility: { field: flag, condition: 'equals', value: true },
});

/** When the run sent it, over the ERP it went to. */
const whenColumn: ListBuilderColumn = {
	id: 'export-log-when',
	fields: [
		{
			...field('export-log-run-started', 'runStartedAt', {
				...plain,
				weight: 'medium',
				formatOptions: { dateFormat: 'datetime-short' },
			}),
			displayStyle: 'datetime',
		},
		lineBreak('export-log-when-break'),
		field('export-log-erp', 'erpName', muted),
	],
	label: 'Sent',
	headers: [{ id: 'h-export-log-when', text: 'Sent' }],
	styleConfig: { size: 'md', colorRule: { type: 'default' } },
	sortable: true,
	sortFields: [{ direction: 'DESC', fieldName: 'runStartedAt', objectTable: OBJ }],
	width: 150,
};

/** The account (links to the client) and its customer number, over the ship-to. */
const accountColumn: ListBuilderColumn = {
	id: 'export-log-account',
	fields: [
		{
			id: 'export-log-client-name',
			link: { type: 'custom', enabled: true, urlTemplate: '/clients/{clientId}' },
			dataConfig: {
				type: 'field',
				field: { fieldName: 'clientName', objectTable: OBJ },
				defaultValue: '-',
			},
			styleConfig: {
				size: 'sm',
				weight: 'medium',
				colorRule: { type: 'theme', themeColor: 'statusInfo' },
				textExpansion: { enabled: true, maxLines: 1 },
			},
			displayStyle: 'text',
		},
		lineBreak('export-log-client-break'),
		{
			// The same C#/L# badge as Recent Changes: the account's customer
			// number, falling back to the internal id.
			id: 'export-log-client-badge',
			dataConfig: {
				type: 'template',
				template:
					'{clientLeadOrCustomer:Customer=C,Lead=L}#{clientExternalId2|clientExternalId|clientId}',
				templateFields: [
					{ fieldName: 'clientLeadOrCustomer' },
					{ fieldName: 'clientExternalId2' },
					{ fieldName: 'clientExternalId' },
					{ fieldName: 'clientId' },
				],
			},
			styleConfig: {
				size: 'xs',
				colorRule: {
					type: 'conditional',
					rules: [
						{
							field: { fieldName: 'clientLeadOrCustomer' },
							operator: 'equals',
							value: 'Customer',
							ignoreCase: true,
							color: { type: 'info' },
						},
						{
							field: { fieldName: 'clientLeadOrCustomer' },
							operator: 'equals',
							value: 'Lead',
							ignoreCase: true,
							color: { type: 'warning' },
						},
					],
				},
				variant: 'solid',
			},
			displayStyle: 'badge',
		},
		lineBreak('export-log-ship-to-break'),
		optionalField('export-log-ship-to', 'shipTo', {
			...muted,
			textExpansion: { enabled: true, maxLines: 1 },
		}),
	],
	label: 'Account / Ship-To',
	headers: [{ id: 'h-export-log-account', text: 'Account / Ship-To' }],
	styleConfig: { size: 'md', colorRule: { type: 'default' } },
	sortable: true,
	sortFields: [{ direction: 'ASC', fieldName: 'clientName', objectTable: OBJ }],
	minWidth: 220,
};

/** The license number sent, over the number the ERP held when it differed. */
const licenseNumberColumn: ListBuilderColumn = {
	id: 'export-log-license-number',
	fields: [
		{
			...field('export-log-number', 'licenseNumber', {
				...plain,
				weight: 'medium',
			}),
			dataConfig: {
				type: 'field',
				field: { fieldName: 'licenseNumber', objectTable: OBJ },
				defaultValue: '-',
			},
		},
		lineBreak('export-log-number-break'),
		labelWhen('export-log-number-was-label', 'was ', 'numberChanged'),
		{
			...field('export-log-number-was', 'previousLicenseNumber', muted),
			conditionalVisibility: {
				field: 'numberChanged',
				condition: 'equals',
				value: true,
			},
		},
	],
	label: 'License #',
	headers: [{ id: 'h-export-log-license-number', text: 'License #' }],
	styleConfig: { size: 'md', colorRule: { type: 'default' } },
	sortable: true,
	sortFields: [
		{ direction: 'ASC', fieldName: 'licenseNumber', objectTable: OBJ },
	],
	width: 150,
};

const typeColumn: ListBuilderColumn = {
	id: 'export-log-type',
	fields: [
		{
			...field('export-log-license-type', 'licenseType', plain),
			dataConfig: {
				type: 'field',
				field: { fieldName: 'licenseType', objectTable: OBJ },
				defaultValue: '-',
			},
		},
	],
	label: 'Type',
	headers: [{ id: 'h-export-log-type', text: 'Type' }],
	styleConfig: { size: 'md', colorRule: { type: 'default' } },
	sortable: true,
	sortFields: [{ direction: 'ASC', fieldName: 'licenseType', objectTable: OBJ }],
	width: 130,
};

const stateColumn: ListBuilderColumn = {
	id: 'export-log-state',
	fields: [
		{
			...field('export-log-license-state', 'state', plain),
			dataConfig: {
				type: 'field',
				field: { fieldName: 'state', objectTable: OBJ },
				defaultValue: '-',
			},
		},
	],
	label: 'State',
	headers: [{ id: 'h-export-log-state', text: 'State' }],
	styleConfig: { size: 'md', colorRule: { type: 'default' } },
	sortable: true,
	sortFields: [{ direction: 'ASC', fieldName: 'state', objectTable: OBJ }],
	width: 80,
};

/**
 * The expiration sent, over the ERP's own date before the write — the
 * Recently Fixed "Was → Now" reading. Only Blue Link is read before writing,
 * so NetSuite and DOit rows show the date sent alone.
 */
const expirationColumn: ListBuilderColumn = {
	id: 'export-log-expiration',
	fields: [
		{
			...field(
				'export-log-expiration-sent',
				'expirationDateSent',
				{ ...plain, weight: 'semibold' },
				'date',
			),
			dataConfig: {
				type: 'field',
				field: { fieldName: 'expirationDateSent', objectTable: OBJ },
				defaultValue: '-',
			},
		},
		lineBreak('export-log-expiration-break'),
		labelWhen('export-log-expiration-was-label', 'was ', 'expirationChanged'),
		{
			...field(
				'export-log-expiration-was',
				'previousExpirationDate',
				muted,
				'date',
			),
			conditionalVisibility: {
				field: 'expirationChanged',
				condition: 'equals',
				value: true,
			},
		},
		{
			id: 'export-log-expiration-new',
			dataConfig: { type: 'custom_text', customText: 'not in the ERP before' },
			styleConfig: muted,
			displayStyle: 'text',
			conditionalVisibility: {
				field: 'result',
				condition: 'equals',
				value: 'Created',
			},
		},
	],
	label: 'Expiration Sent → Was',
	headers: [{ id: 'h-export-log-expiration', text: 'Expiration Sent → Was' }],
	styleConfig: { size: 'md', colorRule: { type: 'default' } },
	sortable: true,
	sortFields: [
		{ direction: 'ASC', fieldName: 'expirationDateSent', objectTable: OBJ },
	],
	width: 170,
};

/** The ERP's answer as a badge, over its message and, for a repeat, how often. */
const resultColumn: ListBuilderColumn = {
	id: 'export-log-result',
	fields: [
		{
			id: 'export-log-result-badge',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'resultLabel', objectTable: OBJ },
			},
			styleConfig: {
				size: 'xs',
				colorRule: {
					type: 'conditional',
					rules: [
						{
							field: { fieldName: 'resultTone' },
							operator: 'equals',
							value: 'danger',
							color: { type: 'danger' },
						},
						{
							field: { fieldName: 'resultTone' },
							operator: 'equals',
							value: 'warning',
							color: { type: 'warning' },
						},
						{
							field: { fieldName: 'resultTone' },
							operator: 'equals',
							value: 'info',
							color: { type: 'info' },
						},
						{
							field: { fieldName: 'resultTone' },
							operator: 'equals',
							value: 'neutral',
							color: { type: 'neutral' },
						},
					],
				},
			},
			displayStyle: 'badge',
		},
		lineBreak('export-log-result-break'),
		optionalField('export-log-result-message', 'resultMessage', {
			...muted,
			allowTextWrap: true,
			textExpansion: { enabled: true, maxLines: 2 },
		}),
		lineBreak('export-log-attempts-break'),
		optionalField('export-log-attempts', 'attemptSummary', {
			size: 'xs',
			colorRule: { type: 'theme', themeColor: 'statusWarning' },
		}),
	],
	label: 'Result',
	headers: [{ id: 'h-export-log-result', text: 'Result' }],
	styleConfig: { size: 'md', colorRule: { type: 'default' } },
	sortable: true,
	sortFields: [{ direction: 'ASC', fieldName: 'result', objectTable: OBJ }],
	minWidth: 220,
	widthConfig: 'large',
};

/** Where the row stands, over who acknowledged it, when, and their note. */
const acknowledgementColumn: ListBuilderColumn = {
	id: 'export-log-acknowledgement',
	fields: [
		{
			id: 'export-log-status',
			dataConfig: {
				type: 'field',
				field: { fieldName: 'statusLabel', objectTable: OBJ },
			},
			styleConfig: {
				size: 'xs',
				colorRule: {
					type: 'conditional',
					rules: [
						{
							field: { fieldName: 'status' },
							operator: 'equals',
							value: 'needs_acknowledgement',
							color: { type: 'warning' },
						},
						{
							field: { fieldName: 'status' },
							operator: 'equals',
							value: 'acknowledged',
							color: { type: 'success' },
						},
						{
							field: { fieldName: 'status' },
							operator: 'equals',
							value: 'no_action',
							color: { type: 'neutral' },
						},
					],
				},
			},
			displayStyle: 'badge',
		},
		lineBreak('export-log-ack-by-break'),
		optionalField('export-log-ack-by', 'acknowledgedByName', muted),
		lineBreak('export-log-ack-at-break'),
		{
			...optionalField('export-log-ack-at', 'acknowledgedAt', {
				...muted,
				formatOptions: { dateFormat: 'datetime-short' },
			}),
			displayStyle: 'datetime',
		},
		lineBreak('export-log-ack-note-break'),
		optionalField('export-log-ack-note', 'acknowledgementNote', {
			...muted,
			allowTextWrap: true,
			textExpansion: { enabled: true, maxLines: 2 },
		}),
	],
	label: 'Acknowledged',
	headers: [{ id: 'h-export-log-acknowledgement', text: 'Acknowledged' }],
	styleConfig: { size: 'md', colorRule: { type: 'default' } },
	sortable: true,
	sortFields: [
		{ direction: 'DESC', fieldName: 'acknowledgedAt', objectTable: OBJ },
	],
	minWidth: 190,
};

/**
 * Acknowledge on a row waiting for someone (it asks for an optional note),
 * Undo on an acknowledged one. Both need licenses:export_log:acknowledge;
 * the API checks it too.
 */
const actionsColumn: ListBuilderColumn = {
	id: 'export-log-actions',
	fields: [
		{
			id: 'export-log-actions-dropdown',
			dataConfig: { type: 'custom_text', customText: '' },
			styleConfig: plain,
			displayStyle: 'actions_dropdown',
			dropdownActions: [
				{
					id: 'export-log-acknowledge',
					label: 'Acknowledge',
					iconName: 'check',
					callbackName: 'acknowledgeExportLogRow',
					variant: 'primary',
					visibility: 'visible',
					showLabel: true,
					requiredPermission: 'licenses:export_log:acknowledge',
					conditionalVisibility: {
						field: 'canAcknowledge',
						condition: 'equals',
						value: true,
					},
				},
				{
					id: 'export-log-undo',
					label: 'Undo',
					iconName: 'rotateLeft',
					callbackName: 'unacknowledgeExportLogRow',
					variant: 'secondary',
					visibility: 'visible',
					showLabel: true,
					requiredPermission: 'licenses:export_log:acknowledge',
					conditionalVisibility: {
						field: 'canUndo',
						condition: 'equals',
						value: true,
					},
				},
			],
		},
	],
	label: '',
	headers: [{ id: 'h-export-log-actions', text: '' }],
	styleConfig: { size: 'md', colorRule: { type: 'default' } },
	alignment: 'right',
	sortable: false,
	width: 150,
};

const COLUMNS: Array<ListBuilderColumn> = [
	whenColumn,
	accountColumn,
	licenseNumberColumn,
	typeColumn,
	stateColumn,
	expirationColumn,
	resultColumn,
	acknowledgementColumn,
	actionsColumn,
];

const unacknowledgedList: ListBuilderList = {
	id: LICENSE_EXPORT_LOG_LIST_IDS.unacknowledged,
	// An acknowledgement moves a row off this tab, so the next read must show it.
	cacheTTL: 0,
	name: 'Unacknowledged',
	type: 'default',
	active: true,
	sortOrder: 1,
	filterRules: {
		exportLogStatus: {
			defaultValue: [
				{ id: 'needs_acknowledgement', label: 'Needs acknowledgement' },
			],
			locked: true,
			hidden: true,
		},
	},
	columns: COLUMNS,
	defaultSortId: 'newest-first',
	bulkActionTemplate: LICENSE_EXPORT_LOG_BULK_TEMPLATE,
};

const allList: ListBuilderList = {
	id: LICENSE_EXPORT_LOG_LIST_IDS.all,
	cacheTTL: 0,
	name: 'All',
	type: 'default',
	active: true,
	sortOrder: 2,
	filterRules: {},
	columns: COLUMNS,
	defaultSortId: 'newest-first',
	bulkActionTemplate: LICENSE_EXPORT_LOG_BULK_TEMPLATE,
};

export const LICENSE_EXPORT_LOG_LIST_CONFIG: ListBuilderConfiguration = {
	lists: [unacknowledgedList, allList],
	paging: { defaultPageSize: 25, pageSizeOptions: [10, 25, 50, 100] },
	showResetButton: true,
	filters: {
		activeFilters: [
			'exportLogKeywords',
			'exportLogStatus',
			'exportLogRunDate',
			'exportLogErp',
			'exportLogResult',
		],
		defaultFilters: {},
	},
	tableSorts: [
		{
			id: 'newest-first',
			name: 'Sent (Newest First)',
			description: 'The most recent pushes first',
			fields: [{ direction: 'DESC', fieldName: 'runStartedAt', objectTable: OBJ }],
		},
		{
			id: 'oldest-first',
			name: 'Sent (Oldest First)',
			description: 'The oldest pushes first',
			fields: [{ direction: 'ASC', fieldName: 'runStartedAt', objectTable: OBJ }],
		},
		{
			id: 'account-az',
			name: 'Account (A to Z)',
			description: 'By account name, newest first within one',
			fields: [
				{ direction: 'ASC', fieldName: 'clientName', objectTable: OBJ },
				{ direction: 'DESC', fieldName: 'runStartedAt', objectTable: OBJ },
			],
		},
	],
	objects: [
		{
			baseUrl: LICENSE_EXPORT_LOG_PATH,
			objectLabel: 'License sent to ERP',
			objectTable: OBJ,
			selectedFields: [
				{ fieldName: 'runStartedAt', fieldLabel: 'Sent' },
				{ fieldName: 'erpName', fieldLabel: 'ERP' },
				{ fieldName: 'clientName', fieldLabel: 'Account' },
				{ fieldName: 'clientExternalId', fieldLabel: 'Customer #' },
				{ fieldName: 'shipTo', fieldLabel: 'Ship-To' },
				{ fieldName: 'licenseType', fieldLabel: 'Type' },
				{ fieldName: 'state', fieldLabel: 'State' },
				{ fieldName: 'licenseNumber', fieldLabel: 'License #' },
				{ fieldName: 'previousLicenseNumber', fieldLabel: 'Previous License #' },
				{ fieldName: 'expirationDateSent', fieldLabel: 'Expiration Sent' },
				{
					fieldName: 'previousExpirationDate',
					fieldLabel: 'Previous Expiration',
				},
				{ fieldName: 'resultLabel', fieldLabel: 'Result' },
				{ fieldName: 'resultMessage', fieldLabel: 'Message' },
				{ fieldName: 'statusLabel', fieldLabel: 'Status' },
				{ fieldName: 'acknowledgedByName', fieldLabel: 'Acknowledged By' },
				{ fieldName: 'acknowledgedAt', fieldLabel: 'Acknowledged At' },
				{ fieldName: 'acknowledgementNote', fieldLabel: 'Note' },
			],
		},
		// The Account column links to /clients/:id.
		{
			baseUrl: '/clients/:id',
			objectLabel: 'Client',
			objectTable: 'client',
			selectedFields: [],
		},
	],
};
